import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MENU_ICON_PATHS } from './menuIcons';

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (nextValues: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  applied?: boolean;
}

const containerStyle: React.CSSProperties = {
  position: 'relative'
};

const buttonBaseStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '14px',
  textAlign: 'left'
};

const panelStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  zIndex: 50,
  backgroundColor: 'white',
  border: '1px solid #ddd',
  borderRadius: '6px',
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
  maxHeight: '240px',
  overflowY: 'auto'
};

const normalizeSelectedValues = (values: string[]) => {
  const seen = new Set<string>();
  const normalized: string[] = [];
  values.forEach((value) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    normalized.push(trimmed);
  });
  return normalized;
};

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = 'All',
  disabled = false,
  applied = false
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
    }
  }, [disabled, open]);

  const selectedLabel = useMemo(() => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const option = options.find((item) => item.value === selectedValues[0]);
      return option?.label || placeholder;
    }
    return `${selectedValues.length} selected`;
  }, [options, placeholder, selectedValues]);

  const allSelected = options.length > 0 && selectedValues.length === options.length;

  const toggleValue = (value: string) => {
    const normalizedSelection = normalizeSelectedValues(selectedValues);
    const normalizedValue = value.trim();
    if (!normalizedValue) return;
    if (normalizedSelection.includes(normalizedValue)) {
      onChange(normalizedSelection.filter((item) => item !== normalizedValue));
    } else {
      onChange(normalizeSelectedValues([...normalizedSelection, normalizedValue]));
    }
  };

  return (
    <div ref={rootRef} style={containerStyle}>
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: '#555',
        marginBottom: '5px'
      }}>
        {label}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        style={{
          ...buttonBaseStyle,
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: disabled ? '#f5f5f5' : (applied ? '#eaf2ff' : 'white'),
          color: disabled ? '#999' : '#333',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedLabel}</span>
        <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <img
            src={open ? MENU_ICON_PATHS.upArrow : MENU_ICON_PATHS.downArrow}
            alt=""
            style={{ width: '11px', height: '11px', objectFit: 'contain' }}
          />
        </span>
      </button>
      {open && !disabled && (
        <div style={panelStyle}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '8px 10px',
            borderBottom: '1px solid #eee',
            fontSize: '12px'
          }}>
            <button
              type="button"
              onClick={() => onChange(normalizeSelectedValues(options.map((item) => item.value)))}
              style={{
                border: 'none',
                background: 'transparent',
                color: allSelected ? '#9ca3af' : '#2563eb',
                cursor: allSelected ? 'default' : 'pointer',
                padding: 0
              }}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => onChange([])}
              style={{
                border: 'none',
                background: 'transparent',
                color: selectedValues.length === 0 ? '#9ca3af' : '#2563eb',
                cursor: selectedValues.length === 0 ? 'default' : 'pointer',
                padding: 0
              }}
            >
              Clear
            </button>
          </div>
          <div style={{ padding: '4px 0' }}>
            {options.length === 0 ? (
              <div style={{ padding: '8px 12px', color: '#6b7280', fontSize: '13px' }}>
                No options available
              </div>
            ) : (
              options.map((option) => (
                <label
                  key={option.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => toggleValue(option.value)}
                  />
                  <span style={{ color: '#333' }}>{option.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
